// /srv/docker/develop/strike.hu_51200-51220/next/components/auth/StrikeAuthMenu.jsx

"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    ArrowLeft,
    LogOut,
    Mail,
    User,
    X,
} from "lucide-react";

import {
    useUser,
} from "@/components/UserContext";

import {
    brandConfig,
} from "@/lib/brandConfig";

const CODE_LENGTH = 6;

function CodeInput({
    value,
    onChange,
    disabled = false,
}) {
    const inputsRef = useRef([]);

    const characters = useMemo(() => {
        const result = new Array(CODE_LENGTH).fill("");

        value
            .slice(0, CODE_LENGTH)
            .split("")
            .forEach((character, index) => {
                result[index] = character;
            });

        return result;
    }, [value]);

    const focusInput = (index) => {
        const input = inputsRef.current[index];

        if (!input) {
            return;
        }

        input.focus();
        input.select();
    };

    const handleChange = (index, nextValue) => {
        const cleanedValue = nextValue
            .replace(/[^a-zA-Z0-9]/g, "")
            .toUpperCase();

        const nextCharacters = [...characters];

        if (!cleanedValue) {
            nextCharacters[index] = "";
            onChange(nextCharacters.join(""));
            return;
        }

        const pastedCharacters = cleanedValue.slice(
            0,
            CODE_LENGTH
        );

        pastedCharacters
            .split("")
            .forEach((character, pastedIndex) => {
                const targetIndex = index + pastedIndex;

                if (targetIndex < CODE_LENGTH) {
                    nextCharacters[targetIndex] =
                        character;
                }
            });

        onChange(nextCharacters.join(""));

        const nextInputIndex = Math.min(
            index + pastedCharacters.length,
            CODE_LENGTH - 1
        );

        window.requestAnimationFrame(() => {
            focusInput(nextInputIndex);
        });
    };

    const handleKeyDown = (index, event) => {
        if (event.key === "Backspace") {
            if (characters[index]) {
                const nextCharacters = [
                    ...characters,
                ];

                nextCharacters[index] = "";
                onChange(nextCharacters.join(""));
                return;
            }

            if (index > 0) {
                const nextCharacters = [
                    ...characters,
                ];

                nextCharacters[index - 1] = "";
                onChange(nextCharacters.join(""));

                window.requestAnimationFrame(() => {
                    focusInput(index - 1);
                });
            }
        }

        if (
            event.key === "ArrowLeft" &&
            index > 0
        ) {
            focusInput(index - 1);
        }

        if (
            event.key === "ArrowRight" &&
            index < CODE_LENGTH - 1
        ) {
            focusInput(index + 1);
        }
    };

    const handlePaste = (event) => {
        event.preventDefault();

        const pastedValue = event.clipboardData
            .getData("text")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toUpperCase()
            .slice(0, CODE_LENGTH);

        onChange(pastedValue);

        const targetIndex = Math.max(
            0,
            Math.min(
                pastedValue.length - 1,
                CODE_LENGTH - 1
            )
        );

        window.requestAnimationFrame(() => {
            focusInput(targetIndex);
        });
    };

    return (
        <div
            className="grid grid-cols-6 gap-2"
            onPaste={handlePaste}
        >
            {characters.map(
                (character, index) => (
                    <input
                        key={index}
                        ref={(element) => {
                            inputsRef.current[index] =
                                element;
                        }}
                        type="text"
                        inputMode="text"
                        autoComplete="one-time-code"
                        maxLength={CODE_LENGTH}
                        value={character}
                        disabled={disabled}
                        onChange={(event) =>
                            handleChange(
                                index,
                                event.target.value
                            )
                        }
                        onKeyDown={(event) =>
                            handleKeyDown(
                                index,
                                event
                            )
                        }
                        aria-label={`Kód ${index + 1
                            }. karakter`}
                        className="
                            h-[54px] min-w-0
                            rounded-[6px] border-2
                            border-[#5b5b5b]
                            bg-[#1a1a1a]
                            text-center text-[18px]
                            font-black uppercase
                            text-white outline-none
                            transition-colors
                            duration-200
                            focus:border-[var(--brand-primary)]
                            disabled:cursor-not-allowed
                            disabled:opacity-50
                            sm:h-[60px]
                            sm:text-[20px]
                        "
                    />
                )
            )}
        </div>
    );
}

export default function StrikeAuthMenu({
    onOpen,
    openTrigger = 0,
    inline = false,
}) {
    const brandStyle = {
        "--brand-primary":
            brandConfig.colors.primary,

        "--brand-primary-hover":
            brandConfig.colors.primaryHover,

        "--brand-background":
            brandConfig.colors.background,

        "--brand-text-on-primary":
            brandConfig.colors.textOnPrimary,

        "--brand-primary-border":
            brandConfig.colors.primaryBorder,

        "--brand-primary-text":
            brandConfig.colors.primaryText,
    };

    const userMenuRef = useRef(null);
    const formId = useId();

    const [isClient, setIsClient] =
        useState(false);

    const [isOpen, setIsOpen] =
        useState(false);

    const {
        user,
        isLoggedIn,
        isUserLoading,
        setUser,
        clearUser,
    } = useUser();

    const [
        isUserMenuOpen,
        setIsUserMenuOpen,
    ] = useState(false);

    const [step, setStep] =
        useState("email");

    const [isLoading, setIsLoading] =
        useState(false);

    const [email, setEmail] =
        useState("");

    const [username, setUsername] =
        useState("");

    const [
        privacyNoticeAcknowledged,
        setPrivacyNoticeAcknowledged,
    ] = useState(false);

    const [code, setCode] =
        useState("");

    const [message, setMessage] =
        useState("");

    const [error, setError] =
        useState("");

    const [
        bumpCloseButton,
        setBumpCloseButton,
    ] = useState(false);

    const hasTypedValue = Boolean(
        email.trim() ||
        username.trim() ||
        code.trim()
    );

    useEffect(() => {
        if (!bumpCloseButton) {
            return;
        }

        const timeout = window.setTimeout(() => {
            setBumpCloseButton(false);
        }, 350);

        return () => {
            window.clearTimeout(timeout);
        };
    }, [bumpCloseButton]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    useEffect(() => {
        if (!isOpen || inline) {
            return;
        }

        const scrollY = window.scrollY;

        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = "0";
        document.body.style.right = "0";
        document.body.style.width = "100%";
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.position = "";
            document.body.style.top = "";
            document.body.style.left = "";
            document.body.style.right = "";
            document.body.style.width = "";
            document.body.style.overflow = "";

            window.scrollTo(0, scrollY);
        };
    }, [isOpen, inline]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                userMenuRef.current &&
                !userMenuRef.current.contains(
                    event.target
                )
            ) {
                setIsUserMenuOpen(false);
            }
        };

        document.addEventListener(
            "mousedown",
            handleClickOutside
        );

        return () => {
            document.removeEventListener(
                "mousedown",
                handleClickOutside
            );
        };
    }, []);

    const resetForm = () => {
        setStep("email");
        setIsLoading(false);
        setEmail("");
        setUsername("");
        setPrivacyNoticeAcknowledged(false);
        setCode("");
        setMessage("");
        setError("");
    };

    const openModal = () => {
        onOpen?.();
        resetForm();
        setIsOpen(true);
    };

    useEffect(() => {
        if (!openTrigger) {
            return;
        }

        openModal();
    }, [openTrigger]);

    const triggerCloseButtonBump = () => {
        setBumpCloseButton(false);

        window.requestAnimationFrame(() => {
            setBumpCloseButton(true);
        });
    };

    const handleBackdropClick = () => {
        if (hasTypedValue) {
            triggerCloseButtonBump();
            return;
        }

        closeModal();
    };

    const closeModal = () => {
        setIsOpen(false);
        resetForm();
    };

    const handleBack = () => {
        setMessage("");
        setError("");
        setCode("");

        if (
            step === "verify" ||
            step === "register"
        ) {
            setStep("email");
            return;
        }

        closeModal();
    };

    const handleRequestCode = async (
        event
    ) => {
        event.preventDefault();

        const normalizedEmail = email
            .trim()
            .toLowerCase();

        if (!normalizedEmail) {
            setError(
                "Add meg az e-mail-címed."
            );

            return;
        }

        setIsLoading(true);
        setMessage("");
        setError("");

        try {
            const response = await fetch(
                "/api/auth/request-code",
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        email: normalizedEmail,
                    }),
                    cache: "no-store",
                }
            );

            const data = await response
                .json()
                .catch(() => ({
                    ok: false,
                    message:
                        "Hiba történt, próbáld meg később.",
                }));

            if (data?.ok !== true) {
                setError(
                    data?.message ||
                        "A kód kiküldése sikertelen."
                );

                return;
            }

            setEmail(normalizedEmail);
            setUsername("");
            setPrivacyNoticeAcknowledged(false);
            setCode("");

            if (data?.mode === "register") {
                setStep("register");
            } else {
                setStep("verify");
            }

            setMessage(
                data?.message ||
                    "A kódot elküldtük e-mailben."
            );
        } catch (requestError) {
            console.error(
                "Request code error:",
                requestError
            );

            setError(
                "A háttérrendszer jelenleg nem érhető el."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleVerifyCode = async (
        event
    ) => {
        event.preventDefault();

        if (code.length !== CODE_LENGTH) {
            setError(
                "Add meg a 6 karakteres kódot."
            );

            return;
        }

        setIsLoading(true);
        setError("");
        setMessage("");

        try {
            const response = await fetch(
                "/api/auth/verify-code",
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        email,
                        code: code.toUpperCase(),
                    }),
                    cache: "no-store",
                }
            );

            const data = await response
                .json()
                .catch(() => ({
                    ok: false,
                    message:
                        "Hiba történt, próbáld meg később.",
                }));

            if (data?.ok !== true) {
                setError(
                    data?.message ||
                        "A megadott kód hibás vagy lejárt."
                );

                return;
            }

            setUser(
                data?.user || {
                    email,
                }
            );

            setIsUserMenuOpen(false);
            setIsOpen(false);

            setStep("email");
            setUsername("");
            setCode("");
            setMessage("");
            setError("");
        } catch (verifyError) {
            console.error(
                "Verify code error:",
                verifyError
            );

            setError(
                "A háttérrendszer jelenleg nem érhető el."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleRegister = async (
        event
    ) => {
        event.preventDefault();

        const normalizedUsername =
            username.trim();

        if (!normalizedUsername) {
            setError(
                "Add meg a felhasználóneved."
            );

            return;
        }

        if (!privacyNoticeAcknowledged) {
            setError(
                "A regisztrációhoz olvasd el és vedd tudomásul az Adatkezelési Tájékoztatót."
            );

            return;
        }

        if (code.length !== CODE_LENGTH) {
            setError(
                "Add meg a 6 karakteres kódot."
            );

            return;
        }

        setIsLoading(true);
        setError("");
        setMessage("");

        try {
            const response = await fetch(
                "/api/auth/verify-code",
                {
                    method: "POST",
                    headers: {
                        "Content-Type":
                            "application/json",
                    },
                    body: JSON.stringify({
                        email,
                        username:
                            normalizedUsername,
                        code: code.toUpperCase(),
                        privacyNoticeAcknowledged:
                            true,
                        privacyNoticeVersion:
                            "2026-08-18",
                    }),
                    cache: "no-store",
                }
            );

            const data = await response
                .json()
                .catch(() => ({
                    ok: false,
                    message:
                        "Hiba történt, próbáld meg később.",
                }));

            if (data?.ok !== true) {
                setError(
                    data?.message ||
                        "A regisztráció sikertelen."
                );

                return;
            }

            setUser(
                data?.user || {
                    email,
                    username:
                        normalizedUsername,
                }
            );

            setIsUserMenuOpen(false);
            setIsOpen(false);

            setStep("email");
            setUsername("");
            setCode("");
            setMessage("");
            setError("");
        } catch (registerError) {
            console.error(
                "Register error:",
                registerError
            );

            setError(
                "A háttérrendszer jelenleg nem érhető el."
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogout = async () => {
        setIsUserMenuOpen(false);

        try {
            const response = await fetch(
                "/api/auth/logout",
                {
                    method: "POST",
                    cache: "no-store",
                }
            );

            const data = await response
                .json()
                .catch(() => ({
                    ok: false,
                }));

            if (data?.ok !== true) {
                console.error(
                    "Logout failed:",
                    data
                );

                return;
            }

            clearUser();
            resetForm();
        } catch (logoutError) {
            console.error(
                "Logout error:",
                logoutError
            );
        }
    };

    const loginPanel = (
                    <div
                        className="
                              relative my-auto w-full
                              max-w-[470px]
                              overflow-hidden
                              rounded-[10px]
                              border border-[#3e3e3e]
                              bg-[#101010]
                              shadow-[0_30px_100px_rgba(0,0,0,0.75)]
                          "
                    >
                        <div className="h-[7px] bg-[var(--brand-primary)]" />

                        <div
                            className="
                                  flex min-h-[74px]
                                  items-center
                                  justify-between
                                  border-b
                                  border-[#343434]
                                  px-4 sm:px-5
                              "
                        >
                            {(!inline || step !== "email") && (<button
                                type="button"
                                onClick={handleBack}
                                className="
                                    flex h-11 w-11
                                    cursor-pointer
                                    items-center
                                    justify-center
                                    rounded-[6px]
                                    bg-[#252525]
                                    text-white
                                    transition-colors
                                    hover:bg-[#353535]
                                    active:scale-95
                                "
                                aria-label="Vissza"
                            >
                                <ArrowLeft size={22} />
                            </button>)}

                            <div className="flex-1 px-3 text-center">
                                <h2
                                    className="
                                          text-[18px] font-black
                                          uppercase leading-tight
                                          text-white sm:text-[20px]
                                      "
                                >
                                    {brandConfig.accountName}
                                </h2>

                                {step === "email" ? (
                                    <p
                                        className="
                                              mt-1 text-[13px]
                                              font-medium text-[#a3a3a3]
                                              sm:text-[14px]
                                          "
                                    >
                                        Regisztrálj vagy jelentkezz be
                                    </p>
                                ) : null}
                            </div>

                            {(!inline) && (<button
                                type="button"
                                onClick={closeModal}
                                className={`
                                    flex cursor-pointer
                                    items-center
                                    justify-center
                                    rounded-[6px]
                                    transition-all
                                    duration-300
                                    ease-out
                                    active:scale-95
                                    ${
                                        bumpCloseButton
                                            ? "h-14 w-14 scale-110 bg-[var(--brand-primary)] text-[var(--brand-text-on-primary)] [box-shadow:0_0_24px_color-mix(in_srgb,var(--brand-primary)_55%,transparent)]"
                                            : "h-11 w-11 bg-[#252525] text-white hover:bg-[#353535]"
                                    }
                                `}
                                aria-label="Bezárás"
                            >
                                <X
                                    size={
                                        bumpCloseButton
                                            ? 30
                                            : 22
                                    }
                                    strokeWidth={2.8}
                                />
                            </button>)}
                        </div>

                        <div className="space-y-5 p-5 sm:p-7">
                            {message ? (
                                <div
                                      className="
                                            rounded-[6px]
                                            border
                                            border-[var(--brand-primary-border)]
                                            bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
                                            px-4 py-3
                                            text-[14px]
                                            font-bold
                                            leading-6
                                            text-[var(--brand-primary-text)]
                                      "
                                >
                                    {message}
                                </div>
                            ) : null}

                            {error ? (
                                <div
                                    className="
                                          rounded-[6px]
                                          border
                                          border-red-800
                                          bg-red-950/50
                                          px-4 py-3
                                          text-[14px]
                                          font-bold
                                          leading-6
                                          text-red-300
                                      "
                                >
                                    {error}
                                </div>
                            ) : null}

                            {step ===
                                "email" ? (
                                <form
                                    onSubmit={
                                        handleRequestCode
                                    }
                                    className="space-y-4"
                                >
                                    <div className="space-y-2">
                                        <label
                                            htmlFor={formId + "-email"}
                                            className="
                                                  block
                                                  text-[14px]
                                                  font-black
                                                  uppercase
                                                  tracking-[0.04em]
                                                  text-white
                                              "
                                        >
                                            E-mail-cím
                                        </label>

                                        <div className="relative">
                                            <Mail
                                                size={
                                                    20
                                                }
                                                className="
                                                      pointer-events-none
                                                      absolute
                                                      left-4 top-1/2
                                                      -translate-y-1/2
                                                      text-[#777777]
                                                  "
                                            />

                                            <input
                                                id={formId + "-email"}
                                                type="email"
                                                value={
                                                    email
                                                }
                                                onChange={(
                                                    event
                                                ) =>
                                                    setEmail(
                                                        event
                                                            .target
                                                            .value
                                                    )
                                                }
                                                placeholder="Add meg az e-mail-címed"
                                                required
                                                autoFocus
                                                className="
                                                      h-[58px]
                                                      w-full
                                                      rounded-[6px]
                                                      border-2
                                                      border-[#4a4a4a]
                                                      bg-[#1b1b1b]
                                                      pl-12 pr-4
                                                      text-[16px]
                                                      font-semibold
                                                      text-white
                                                      outline-none
                                                      transition-colors
                                                      placeholder:text-[#707070]
                                                      focus:border-[var(--brand-primary)]
                                                  "
                                            />
                                        </div>
                                    </div>                                    

                                    <button
                                        type="submit"
                                        disabled={
                                            isLoading
                                        }
                                        className="
                                              flex min-h-[56px]
                                              w-full
                                              cursor-pointer
                                              items-center
                                              justify-center
                                              rounded-[6px]
                                              bg-[var(--brand-primary)]
                                              px-5
                                              text-[17px]
                                              font-black
                                              text-[var(--brand-text-on-primary)]
                                              transition-transform
                                              duration-200
                                              hover:scale-[1.02]
                                              active:translate-y-[2px]
                                              active:scale-[0.98]
                                              disabled:cursor-not-allowed
                                              disabled:opacity-60
                                          "
                                    >
                                        {isLoading
                                            ? "Ellenőrzés..."
                                            : "Folytatás"}
                                    </button>

                                    <p
                                        className="
                                              text-[12px]
                                              leading-5
                                              text-[#777777]
                                          "
                                    >
                                        A folytatással megerősíted, hogy
                                        elmúltál 16 éves, és egyetértesz az{" "}
                                        <a
                                            href="/aszf"
                                            className="
                                                  cursor-pointer
                                                  font-semibold
                                                  text-[#a8a8a8]
                                                  underline
                                                  underline-offset-2
                                                  transition-colors
                                                  hover:text-white
                                              "
                                        >
                                            Általános Szerződési Feltételekkel
                                        </a>
                                        .
                                    </p>
                                </form>
                            ) : null}

                            {step ===
                                "verify" ? (
                                <form
                                    onSubmit={
                                        handleVerifyCode
                                    }
                                    className="space-y-5"
                                >
                                    <div className="space-y-4">
                                        <p
                                            className="
                                                text-center
                                                text-[15px]
                                                leading-6
                                                text-[#adadad]
                                            "
                                        >
                                            Alább írd be az e-mailben található kódot.
                                        </p>

                                        <div className="space-y-2">
                                            <label
                                                className="
                                                    block
                                                    text-[14px]
                                                    font-black
                                                    uppercase
                                                    tracking-[0.04em]
                                                    text-white
                                                "
                                            >
                                                E-mail-cím
                                            </label>

                                            <div
                                                className="
                                                    flex h-[58px]
                                                    w-full
                                                    cursor-default
                                                    items-center
                                                    rounded-[6px]
                                                    border-2
                                                    border-[#666666]
                                                    bg-[#3a3a3a]
                                                    px-4
                                                    select-none
                                                "
                                                aria-readonly="true"
                                            >
                                                <Mail
                                                    size={20}
                                                    className="
                                                        mr-3
                                                        shrink-0
                                                        text-[#9a9a9a]
                                                    "
                                                />

                                                <span
                                                    className="
                                                        min-w-0
                                                        flex-1
                                                        truncate
                                                        text-[16px]
                                                        font-semibold
                                                        text-[#b8b8b8]
                                                    "
                                                >
                                                    {email}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            className="
                                                  block
                                                  text-[14px]
                                                  font-black
                                                  uppercase
                                                  tracking-[0.04em]
                                                  text-white
                                              "
                                        >
                                            Kód
                                        </label>

                                        <CodeInput
                                            value={
                                                code
                                            }
                                            onChange={
                                                setCode
                                            }
                                            disabled={
                                                isLoading
                                            }
                                        />

                                        <p className="text-[12px] text-[#858585]">
                                            Add meg a
                                            6
                                            karakteres
                                            kódot.
                                        </p>
                                    </div>

                                    <label
                                        className="
                                            flex cursor-pointer
                                            items-start gap-3
                                            rounded-[6px]
                                            border border-[#3f3f3f]
                                            bg-[#1b1b1b]
                                            p-4
                                        "
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                privacyNoticeAcknowledged
                                            }
                                            onChange={(
                                                event
                                            ) => {
                                                setPrivacyNoticeAcknowledged(
                                                    event
                                                        .target
                                                        .checked
                                                );
                                                setError(
                                                    ""
                                                );
                                            }}
                                            required
                                            className="
                                                mt-1 h-4 w-4
                                                shrink-0
                                                cursor-pointer
                                                accent-[var(--brand-primary)]
                                            "
                                        />

                                        <span
                                            className="
                                                text-[13px]
                                                leading-5
                                                text-[#b8b8b8]
                                            "
                                        >
                                            Elolvastam és
                                            tudomásul vettem az{" "}
                                            <a
                                                href="/adatkezelesi-tajekoztato"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(
                                                    event
                                                ) =>
                                                    event.stopPropagation()
                                                }
                                                className="
                                                    cursor-pointer
                                                    font-bold
                                                    text-[var(--brand-primary)]
                                                    underline
                                                    underline-offset-2
                                                "
                                            >
                                                Adatkezelési
                                                Tájékoztatót
                                            </a>
                                            , ideértve azt is,
                                            hogy a regisztrált
                                            fiókban a rendelés
                                            során megadott
                                            kapcsolattartási és
                                            számlázási adatok a
                                            későbbi rendelések
                                            megkönnyítése
                                            érdekében
                                            automatikusan
                                            mentésre és
                                            visszatöltésre
                                            kerülnek.
                                        </span>
                                    </label>

                                    <button
                                        type="submit"
                                        disabled={
                                            isLoading ||
                                            code.length !==
                                            CODE_LENGTH
                                        }
                                        className="
                                              flex min-h-[56px]
                                              w-full
                                              cursor-pointer
                                              items-center
                                              justify-center
                                              rounded-[6px]
                                              bg-[var(--brand-primary)]
                                              px-5
                                              text-[17px]
                                              font-black
                                              text-[var(--brand-text-on-primary)]
                                              transition-transform
                                              duration-200
                                              hover:scale-[1.02]
                                              active:translate-y-[2px]
                                              active:scale-[0.98]
                                              disabled:cursor-not-allowed
                                              disabled:opacity-50
                                          "
                                    >
                                        {isLoading
                                            ? "Belépés..."
                                            : "Belépés"}
                                    </button>
                                </form>
                            ) : null}

                            {step ===
                                "register" ? (
                                <form
                                    onSubmit={
                                        handleRegister
                                    }
                                    className="space-y-5"
                                >
                                    <div className="space-y-4">
                                        <p
                                            className="
                                                text-center
                                                text-[15px]
                                                leading-6
                                                text-[#adadad]
                                            "
                                        >
                                            Ez az e-mail-cím még nincs regisztrálva.
                                            A fiók létrehozásához adj meg egy
                                            felhasználónevet és az e-mailben kapott
                                            megerősítő kódot.
                                        </p>

                                        <div className="space-y-2">
                                            <label
                                                className="
                                                    block
                                                    text-[14px]
                                                    font-black
                                                    uppercase
                                                    tracking-[0.04em]
                                                    text-white
                                                "
                                            >
                                                E-mail-cím
                                            </label>

                                            <div
                                                className="
                                                    flex h-[58px]
                                                    w-full
                                                    cursor-default
                                                    items-center
                                                    rounded-[6px]
                                                    border-2
                                                    border-[#666666]
                                                    bg-[#3a3a3a]
                                                    px-4
                                                    select-none
                                                "
                                                aria-readonly="true"
                                            >
                                                <Mail
                                                    size={20}
                                                    className="
                                                        mr-3
                                                        shrink-0
                                                        text-[#9a9a9a]
                                                    "
                                                />

                                                <span
                                                    className="
                                                        min-w-0
                                                        flex-1
                                                        truncate
                                                        text-[16px]
                                                        font-semibold
                                                        text-[#b8b8b8]
                                                    "
                                                >
                                                    {email}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            htmlFor={formId + "-username"}
                                            className="
                                                  block
                                                  text-[14px]
                                                  font-black
                                                  uppercase
                                                  tracking-[0.04em]
                                                  text-white
                                              "
                                        >
                                            Felhasználónév
                                        </label>

                                        <input
                                            id={formId + "-username"}
                                            type="text"
                                            value={
                                                username
                                            }
                                            onChange={(
                                                event
                                            ) =>
                                                setUsername(
                                                    event
                                                        .target
                                                        .value
                                                )
                                            }
                                            placeholder="Találj ki egy felhasználónevet"
                                            required
                                            autoFocus
                                            className="
                                                  h-[58px]
                                                  w-full
                                                  rounded-[6px]
                                                  border-2
                                                  border-[#4a4a4a]
                                                  bg-[#1b1b1b]
                                                  px-4
                                                  text-[16px]
                                                  font-semibold
                                                  text-white
                                                  outline-none
                                                  transition-colors
                                                  placeholder:text-[#707070]
                                                  focus:border-[var(--brand-primary)]
                                              "
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label
                                            className="
                                                  block
                                                  text-[14px]
                                                  font-black
                                                  uppercase
                                                  tracking-[0.04em]
                                                  text-white
                                              "
                                        >
                                            Kód
                                        </label>

                                        <CodeInput
                                            value={
                                                code
                                            }
                                            onChange={
                                                setCode
                                            }
                                            disabled={
                                                isLoading
                                            }
                                        />

                                        <p className="text-[12px] text-[#858585]">
                                            Add meg a
                                            6
                                            karakteres
                                            kódot.
                                        </p>
                                    </div>

                                    <label
                                        className="
                                            flex cursor-pointer
                                            items-start gap-3
                                            rounded-[6px]
                                            border border-[#3f3f3f]
                                            bg-[#1b1b1b]
                                            p-4
                                        "
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                privacyNoticeAcknowledged
                                            }
                                            onChange={(
                                                event
                                            ) => {
                                                setPrivacyNoticeAcknowledged(
                                                    event
                                                        .target
                                                        .checked
                                                );
                                                setError(
                                                    ""
                                                );
                                            }}
                                            required
                                            className="
                                                mt-1 h-4 w-4
                                                shrink-0
                                                cursor-pointer
                                                accent-[var(--brand-primary)]
                                            "
                                        />

                                        <span
                                            className="
                                                text-[13px]
                                                leading-5
                                                text-[#b8b8b8]
                                            "
                                        >
                                            Elolvastam és
                                            tudomásul vettem az{" "}
                                            <a
                                                href="/adatkezelesi-tajekoztato"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(
                                                    event
                                                ) =>
                                                    event.stopPropagation()
                                                }
                                                className="
                                                    cursor-pointer
                                                    font-bold
                                                    text-[var(--brand-primary)]
                                                    underline
                                                    underline-offset-2
                                                "
                                            >
                                                Adatkezelési
                                                Tájékoztatót
                                            </a>
                                            , ideértve azt is,
                                            hogy a regisztrált
                                            fiókban a rendelés
                                            során megadott
                                            kapcsolattartási és
                                            számlázási adatok a
                                            későbbi rendelések
                                            megkönnyítése
                                            érdekében
                                            automatikusan
                                            mentésre és
                                            visszatöltésre
                                            kerülnek.
                                        </span>
                                    </label>

                                    <button
                                        type="submit"
                                        disabled={
                                            isLoading ||
                                            !username.trim() ||
                                            !privacyNoticeAcknowledged ||
                                            code.length !==
                                            CODE_LENGTH
                                        }
                                        className="
                                              flex min-h-[56px]
                                              w-full
                                              cursor-pointer
                                              items-center
                                              justify-center
                                              rounded-[6px]
                                              bg-[var(--brand-primary)]
                                              px-5
                                              text-[17px]
                                              font-black
                                              text-[var(--brand-text-on-primary)]
                                              transition-transform
                                              duration-200
                                              hover:scale-[1.02]
                                              active:translate-y-[2px]
                                              active:scale-[0.98]
                                              disabled:cursor-not-allowed
                                              disabled:opacity-50
                                          "
                                    >
                                        {isLoading
                                            ? "Fiók létrehozása..."
                                            : "Fiók létrehozása"}
                                    </button>
                                </form>
                            ) : null}
                        </div>
                    </div>
    );

    const modal = isClient && isOpen && !inline
        ? createPortal(
                <div
                    style={brandStyle}
                    className="
                          fixed inset-0 z-[99999]
                          flex items-center
                          justify-center
                          overflow-y-auto
                          bg-black/80 p-4
                          backdrop-blur-[5px]
                      "
                    onMouseDown={(event) => {
                        if (
                            event.target ===
                            event.currentTarget
                        ) {
                            handleBackdropClick();
                        }
                    }}
                >
                    {loginPanel}
                </div>,
                document.body
            )
        : null;

    if (inline) {
        return <div style={brandStyle}>{loginPanel}</div>;
    }

    return (
        <>

           

            <div
                ref={userMenuRef}
                style={brandStyle}
                className="relative"
            >
                {isUserLoading ? (
                    <div
                        className="
                            flex h-11 w-11
                            items-center
                            justify-center
                            text-white/40
                        "
                        aria-label="Felhasználó betöltése"
                    >
                        <User
                            size={33}
                            strokeWidth={2.8}
                        />
                    </div>
                ) : isLoggedIn ? (
                    <>
                        <button
                            type="button"
                            onClick={() => {
                                onOpen?.();

                                setIsUserMenuOpen(
                                    (current) =>
                                        !current
                                );
                            }}
                            className="
                                flex h-11 w-11
                                cursor-pointer
                                items-center
                                justify-center
                                text-[var(--brand-primary)]
                                select-none
                                [-webkit-touch-callout:none]
                            "
                            aria-label="Profilmenü megnyitása"
                            aria-expanded={
                                isUserMenuOpen
                            }
                        >
                            <User
                                size={33}
                                strokeWidth={2.8}
                            />
                        </button>

                        {isUserMenuOpen ? (
                            <div
                                className="
                                    absolute right-0
                                    top-full z-[120]
                                    mt-3 w-[220px]
                                    overflow-hidden
                                    rounded-[6px]
                                    border
                                    border-[#3f3f3f]
                                    bg-[#151515]
                                    shadow-[0_20px_60px_rgba(0,0,0,0.65)]
                                "
                            >
                                <div
                                    className="
                                        border-b
                                        border-[#353535]
                                        px-4 py-3
                                    "
                                >
                                    <p
                                        className="
                                            text-[12px]
                                            font-bold
                                            uppercase
                                            tracking-[0.08em]
                                            text-[#7f7f7f]
                                        "
                                    >
                                        Bejelentkezve
                                    </p>

                                    <p
                                        className="
                                            mt-1 truncate
                                            text-[14px]
                                            font-black
                                            text-white
                                        "
                                    >
                                        {user?.username ||
                                            user?.email ||
                                            brandConfig.defaultUserName}
                                    </p>
                                </div>

                                {user?.isMarketing === true && (
                                    <a href="/marketing" className="block px-4 py-4 text-[15px] font-bold text-white transition-colors hover:bg-[#292929]">
                                        marketing
                                    </a>
                                )}

                                <button
                                    type="button"
                                    onClick={
                                        handleLogout
                                    }
                                    className="
                                        flex w-full
                                        cursor-pointer
                                        items-center
                                        gap-3 px-4 py-4
                                        text-left
                                        text-[15px]
                                        font-bold
                                        text-white
                                        transition-colors
                                        hover:bg-[#292929]
                                    "
                                >
                                    <LogOut
                                        size={19}
                                    />

                                    <span>
                                        Kijelentkezés
                                    </span>
                                </button>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <button
                        type="button"
                        onClick={openModal}
                        className="
                            flex h-11 w-11
                            cursor-pointer
                            items-center
                            justify-center
                            text-white
                            select-none
                            [-webkit-touch-callout:none]
                        "
                        aria-label="Bejelentkezés"
                        title="Bejelentkezés"
                    >
                        <User
                            size={33}
                            strokeWidth={2.8}
                        />
                    </button>
                )}
            </div>

            {modal}
        </>
    );
}